import React, { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api, { formatApiError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", role: "employee", phone: "", designation: "" };

function initials(n = "") {
    return n.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

export default function Employees() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);

    const load = () => api.get("/employees").then((r) => setItems(r.data));

    useEffect(() => {
        load();
    }, []);

    const save = async () => {
        try {
            if (editing) {
                const payload = { ...form };
                delete payload.email;
                delete payload.password;
                await api.patch(`/employees/${editing.id}`, payload);
            } else {
                await api.post("/employees", form);
            }
            toast.success(editing ? "Employee updated" : "Employee added");
            setOpen(false);
            setForm(EMPTY);
            setEditing(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Deactivate this employee?")) return;
        try {
            await api.delete(`/employees/${id}`);
            toast.success("Employee removed");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-6" data-testid="employees-page">
            <PageHeader
                title="Team"
                subtitle="Employees & admins who can access this firm"
                actions={
                    <Button
                        onClick={() => {
                            setEditing(null);
                            setForm(EMPTY);
                            setOpen(true);
                        }}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="new-employee-btn"
                    >
                        <Plus size={16} className="mr-1.5" /> Add employee
                    </Button>
                }
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((e) => (
                    <Card
                        key={e.id}
                        className="p-5 hover:shadow-md hover:-translate-y-0.5 transition-all"
                        data-testid={`employee-card-${e.id}`}
                    >
                        <div className="flex items-start gap-3">
                            <Avatar className="h-11 w-11">
                                <AvatarFallback className="bg-primary text-white font-semibold">
                                    {initials(e.name)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-slate-800 truncate">{e.name}</div>
                                <div className="text-xs text-slate-500 truncate">{e.email}</div>
                                <div className="mt-2 flex items-center gap-2 flex-wrap">
                                    <Badge className="badge-status bg-blue-50 text-primary border-blue-100">
                                        {e.role}
                                    </Badge>
                                    {e.designation && (
                                        <span className="text-xs text-slate-500">{e.designation}</span>
                                    )}
                                    {e.active === false && (
                                        <span className="text-xs text-rose-500">disabled</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col gap-1">
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => {
                                        setEditing(e);
                                        setForm({ ...EMPTY, ...e, password: "" });
                                        setOpen(true);
                                    }}
                                >
                                    <Pencil size={14} />
                                </Button>
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => remove(e.id)}
                                    className="text-rose-600 hover:bg-rose-50"
                                >
                                    <Trash2 size={14} />
                                </Button>
                            </div>
                        </div>
                    </Card>
                ))}
                {items.length === 0 && (
                    <div className="text-slate-500 text-sm">No employees yet.</div>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">
                            {editing ? "Edit employee" : "Add employee"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-4">
                        <F label="Name">
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </F>
                        <F label="Email">
                            <Input
                                type="email"
                                value={form.email}
                                disabled={!!editing}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                            />
                        </F>
                        {!editing && (
                            <F label="Password">
                                <Input
                                    type="password"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                />
                            </F>
                        )}
                        <F label="Role">
                            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="employee">Employee</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Phone">
                            <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        </F>
                        <F label="Designation">
                            <Input
                                value={form.designation || ""}
                                onChange={(e) => setForm({ ...form, designation: e.target.value })}
                            />
                        </F>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-employee">
                            {editing ? "Save" : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const F = ({ label, children }) => (
    <div className="space-y-1.5">
        <Label className="field-label">{label}</Label>
        {children}
    </div>
);
